/**
 * ReportActionButtons — 报告收藏 + 关注 按钮组
 *
 * 供 MarketInsightCard 与 MarketInsightDetail 共享，保持视觉一致。
 * 样式基准：卡片侧（SVG 图标 + Tooltip，Follow 已关注态绿色高亮）
 */

import { useCallback } from 'react';
import cn from 'classnames';
import { Loader2 } from 'lucide-react';
import { BaseTooltip } from '@/common/base/tooltip';
import StarOutlineIcon from '@/assets/icons/star-outline.svg?react';
import StarSelectedIcon from '@/assets/icons/star-selected.svg?react';
import FollowOutlineIcon from '@/assets/icons/follow-outline.svg?react';
import FollowSelectedIcon from '@/assets/icons/follow-selected.svg?react';
import type { FollowStatus } from '../detail/types';

export interface ReportActionButtonsProps {
  /** 是否已收藏 */
  isSaved: boolean;
  /** 收藏操作进行中 */
  saveLoading: boolean;
  /** Topic 关注状态 */
  followStatus: FollowStatus;
  /** 关注操作进行中 */
  followLoading: boolean;
  /** 切换收藏 */
  onToggleSave: () => void;
  /** 切换关注 */
  onToggleFollow: () => void;
  /** 外部附加 className（作用于整个按钮组容器） */
  className?: string;
  flowBtnClassName?: string;
  /** 是否反转按钮顺序（默认：Save → Follow；true：Follow → Save） */
  reverseOrder?: boolean;
}

export function ReportActionButtons({
  isSaved,
  saveLoading,
  followStatus,
  followLoading,
  onToggleSave,
  onToggleFollow,
  className,
  flowBtnClassName,
  reverseOrder = false,
}: ReportActionButtonsProps) {
  const isFollowing = followStatus === 1;

  const handleSave = useCallback(() => {
    onToggleSave();
  }, [onToggleSave]);

  const handleFollow = useCallback(() => {
    onToggleFollow();
  }, [onToggleFollow]);

  const saveBtn = (
    <BaseTooltip content={isSaved ? undefined : 'Save Report'}>
      <button
        type="button"
        onClick={handleSave}
        disabled={saveLoading}
        className="w-[34px] h-[34px] border border-[#E5E5E5] rounded-[8px] flex items-center justify-center bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
        aria-label={isSaved ? '取消收藏' : '收藏报告'}
      >
        {saveLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : isSaved ? (
          <StarSelectedIcon className="size-4" />
        ) : (
          <StarOutlineIcon className="size-4" />
        )}
      </button>
    </BaseTooltip>
  );

  const followBtn = (
    <BaseTooltip content={isFollowing ? undefined : 'Follow for Weekly Updates'}>
      <button
        type="button"
        onClick={handleFollow}
        disabled={followLoading}
        className={cn(
          'border rounded-[8px] px-4 py-[5.5px] text-[14px] font-normal text-[#262626] flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed',
          flowBtnClassName,
          isFollowing
            ? 'border-[rgba(4,120,87,0.1)] bg-[#ECFDF5] hover:opacity-90'
            : 'border-[#E5E5E5] bg-white hover:bg-gray-50'
        )}
        aria-label={isFollowing ? '取消关注' : '关注 Topic'}
      >
        <span>{isFollowing ? 'Followed' : 'Follow'}</span>
        {followLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : isFollowing ? (
          <FollowSelectedIcon className="size-4" />
        ) : (
          <FollowOutlineIcon className="size-4" />
        )}
      </button>
    </BaseTooltip>
  );

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {reverseOrder ? (
        <>
          {followBtn}
          {saveBtn}
        </>
      ) : (
        <>
          {saveBtn}
          {followBtn}
        </>
      )}
    </div>
  );
}
